import { useEffect, useState } from "react";
import { isPastDate } from "../model/followUpRules";
import {
    addPhoneButtonStyle,
    checkboxCellStyle,
    checkboxInputStyle,
    compoundCellStyle,
    dateInputWrapperStyle,
    inputStyle,
    pastDateIndicatorStyle,
    phoneRowStyle,
    removePhoneButtonStyle,
} from "../styles/materielStyles";

function SelectOptions({ options }) {
    return options.map(option => <option key={option} value={option}>{option || "-"}</option>);
}

// Caution : « Montant » ouvre la saisie du montant, tout autre choix le vide.
function DepositCell({ row, column, value, onChange }) {
    return (
        <div style={compoundCellStyle}>
            <select
                value={value}
                onChange={e => onChange({
                    deposit: e.target.value,
                    depositAmount: e.target.value === "Montant" ? (row.depositAmount ?? "") : "",
                })}
                style={inputStyle}
            >
                <SelectOptions options={column.options} />
            </select>
            {value === "Montant" && (
                <input
                    type="text"
                    value={row.depositAmount ?? ""}
                    onChange={e => onChange({ deposit: value, depositAmount: e.target.value })}
                    placeholder="Montant"
                    style={inputStyle}
                />
            )}
        </div>
    );
}

function DateRangeCell({ row, column, value, onChange }) {
    return (
        <div style={compoundCellStyle}>
            <DateInputWithPastIndicator value={value} onChange={next => onChange(next)} ariaLabel="Date de début ordonnance" />
            <DateInputWithPastIndicator
                value={row[column.endKey] ?? ""}
                onChange={next => onChange({ [column.key]: value, [column.endKey]: next })}
                ariaLabel="Date de fin ordonnance"
            />
        </div>
    );
}

function TextDateCell({ row, column, value, onChange }) {
    return (
        <div style={compoundCellStyle}>
            <input type="text" value={value} onChange={e => onChange(e.target.value)} style={inputStyle} />
            <DateInputWithPastIndicator
                value={row[column.dateKey] ?? ""}
                onChange={next => onChange({ [column.key]: value, [column.dateKey]: next })}
                ariaLabel="Date envoi poste"
            />
        </div>
    );
}

// Champ d'une colonne de tableau selon son type. Un champ composé renvoie un objet de plusieurs valeurs.
export function Cell({ row, column, onChange, checkboxAlign = "left" }) {
    const value = row[column.key] ?? "";
    const props = { row, column, value, onChange };
    switch (column.type) {
        case "checkbox":
            return (
                <span style={checkboxCellStyle(checkboxAlign)}>
                    <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} style={checkboxInputStyle} />
                </span>
            );
        case "phones":
            return <PhoneNumbersCell value={Array.isArray(value) ? value : []} onChange={onChange} />;
        case "select":
            return (
                <select value={value} onChange={e => onChange(e.target.value)} style={inputStyle}>
                    <SelectOptions options={column.options} />
                </select>
            );
        case "deposit":
            return <DepositCell {...props} />;
        case "dateRange":
            return <DateRangeCell {...props} />;
        case "textDate":
            return <TextDateCell {...props} />;
        case "date":
            return <DateInputWithPastIndicator value={value} onChange={onChange} ariaLabel={column.label} />;
        default:
            return <input type={column.type || "text"} value={value} onChange={e => onChange(e.target.value)} style={inputStyle} />;
    }
}

function DateInputWithPastIndicator({ value, onChange, ariaLabel }) {
    return (
        <div style={dateInputWrapperStyle}>
            <input
                type="date"
                value={value}
                onChange={event => onChange(event.target.value)}
                aria-label={ariaLabel}
                style={inputStyle}
            />
            {isPastDate(value) && <span title="Date passée" aria-label="Date passée" style={pastDateIndicatorStyle}>⛔</span>}
        </div>
    );
}

// Numéros de téléphone : la saisie reste locale et n'est enregistrée qu'à la sortie du champ, numéros vides retirés.
function PhoneNumbersCell({ value, onChange }) {
    const [numbers, setNumbers] = useState(value);

    useEffect(() => {
        setNumbers(value);
    }, [value]);

    const save = next => {
        const cleaned = next.map(number => number.trim()).filter(Boolean);
        setNumbers(cleaned);
        onChange(cleaned);
    };

    const updateNumber = (index, number) => {
        setNumbers(current => current.map((item, itemIndex) => itemIndex === index ? number : item));
    };

    const removeNumber = index => {
        save(numbers.filter((_, itemIndex) => itemIndex !== index));
    };

    return (
        <div style={compoundCellStyle}>
            {numbers.map((number, index) => (
                <div key={index} style={phoneRowStyle}>
                    <input
                        type="tel"
                        aria-label={`Numéro de téléphone ${index + 1}`}
                        value={number}
                        onChange={event => updateNumber(index, event.target.value)}
                        onBlur={() => save(numbers)}
                        style={inputStyle}
                    />
                    <button
                        type="button"
                        aria-label={`Supprimer le numéro ${index + 1}`}
                        title="Supprimer ce numéro"
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => removeNumber(index)}
                        style={removePhoneButtonStyle}
                    >
                        ×
                    </button>
                </div>
            ))}
            <button
                type="button"
                onClick={() => setNumbers(current => [...current, ""])}
                style={addPhoneButtonStyle}
            >
                + Ajouter un numéro
            </button>
        </div>
    );
}
